/* eslint-disable no-undef */
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import * as CryptoJS from "crypto-js";

export const ParamKey = {
  TicketId: "TicketId",
  Phases: "Phases",
  Statuses: "Statuses",
  OtherFilter: "OtherFilter",
  Sort: "Sort",
  Page: "page",
  Menu: "menu",
};

export const useQueryParams = () => {
  const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

  const [searchParams, setSearchParams] = useSearchParams();

  const createSecretKey = () => {
    const randomBytes = new Uint8Array(32);
    crypto.getRandomValues(randomBytes);

    return Array.from(randomBytes)
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  };

  const encryptValue = useCallback(
    (value) => {
      if (!SECRET_KEY) {
        return value;
      }

      if (!value) return value;

      try {
        const encrypted = CryptoJS.AES.encrypt(
          String(value),
          SECRET_KEY
        ).toString();
        return encrypted;
      } catch (error) {
        return value;
      }
    },
    [SECRET_KEY]
  );

  const decryptValue = useCallback(
    (encryptedValue) => {
      if (!SECRET_KEY) {
        return encryptedValue;
      }

      if (!encryptedValue) return encryptedValue;

      try {
        const bytes = CryptoJS.AES.decrypt(encryptedValue, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        return decrypted || encryptedValue;
      } catch (error) {
        return encryptedValue;
      }
    },
    [SECRET_KEY]
  );

  const getParam = useCallback(
    (key) => {
      const encryptedValue = searchParams.get(key);
      if (!encryptedValue) return null;
      return decryptValue(encryptedValue);
    },
    [searchParams, decryptValue]
  );

  const getAllParams = useCallback(() => {
    const params = {};
    for (const [key, encryptedValue] of searchParams.entries()) {
      params[key] = decryptValue(encryptedValue);
    }
    return params;
  }, [searchParams, decryptValue]);

  const setParam = useCallback(
    (key, value) => {
      const newParams = new URLSearchParams(searchParams);

      if (value === null || value === undefined || value === "") {
        newParams.delete(key);
      } else {
        const encryptedValue = encryptValue(value);
        newParams.set(key, encryptedValue);
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, encryptValue]
  );

  const setParams = useCallback(
    (paramsObject) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(paramsObject).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          newParams.delete(key);
        } else {
          const encryptedValue = encryptValue(value);
          newParams.set(key, encryptedValue);
        }
      });

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, encryptValue]
  );

  const appendParams = useCallback(
    (paramsObject) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(paramsObject).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          const encryptedValue = encryptValue(value);
          newParams.set(key, encryptedValue);
        }
      });

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, encryptValue]
  );

  const removeParams = useCallback(
    (keys) => {
      const newParams = new URLSearchParams(searchParams);

      if (Array.isArray(keys)) {
        keys.forEach((key) => newParams.delete(key));
      } else {
        newParams.delete(keys);
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const clearParams = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const hasParam = useCallback(
    (key) => {
      return searchParams.has(key);
    },
    [searchParams]
  );

  const getRawParam = useCallback(
    (key) => {
      return searchParams.get(key);
    },
    [searchParams]
  );

  return {
    createSecretKey,

    getParam,
    getAllParams,
    setParam,
    setParams,
    appendParams,
    removeParams,
    clearParams,
    hasParam,
    getRawParam,

    encryptValue,
    decryptValue,

    searchParams,
    setSearchParams,
  };
};
